import {
  BlockEditProps,
  createBlock,
  registerBlockType,
} from "@wordpress/blocks";
import React, { useCallback, useLayoutEffect, useRef } from "react";
import config, { type BlockAttributes } from "./block.json";
import {
  InnerBlocks,
  useBlockProps,
  useInnerBlocksProps,
  store as blockEditorStore,
} from "@wordpress/block-editor";
import { useDispatch } from "@wordpress/data";
import useBlockEditorSelect from "../../../source/hooks/useBlockEditorSelect";
import Swiper from "swiper";
import { SliderIcon } from "../../../source/icons";
import "./swiper";
import { SwiperOptions } from "swiper/types";
import { cardSliderOptions } from "../../../../../frontend/scripts/sliderOptions";
import { teaserCardSliderOptions } from "../../../../../frontend/scripts/teaserSliderOptions";
import { addFilter } from "@wordpress/hooks";
import { createHigherOrderComponent } from "@wordpress/compose";
import useParentBlockName from "../../../source/hooks/useParentBlockName";
import { registerBlockVariations } from "./variations";
import { JSX } from "react/jsx-runtime";

export default function domReady(
  el: HTMLElement | null | undefined,
  callback: () => unknown
) {
  const doc = el?.ownerDocument;
  if (typeof doc === "undefined") {
    return;
  }

  if (
    doc.readyState === "complete" || // DOMContentLoaded + Images/Styles/etc loaded, so we call directly.
    doc.readyState === "interactive" // DOMContentLoaded fires at this point, so we call directly.
  ) {
    return void callback();
  }

  // DOMContentLoaded has not fired yet, delay callback until then.
  doc.addEventListener("DOMContentLoaded", callback);
}

const swiperParams: SwiperOptions = {
  ...cardSliderOptions,
  touchStartPreventDefault: false,
  injectStyles: [
    ...(cardSliderOptions.injectStyles ?? []),
    `
      .swiper {
        padding: 1px;
        margin: -1px;
      }
    `,
  ],
};

const swiperTeaserParams: SwiperOptions = {
  ...teaserCardSliderOptions,
  touchStartPreventDefault: false,
  injectStyles: [
    ...(teaserCardSliderOptions.injectStyles ?? []),
    `
      .swiper {
        padding: 1px;
        margin: -1px;
      }
    `,
  ],
};

function initSwiper(el: Element | null | undefined) {
  if (!el) {
    return;
  }
  const sliderType = el
    .querySelector("swiper-container > swiper-slide > div")
    ?.getAttribute("data-type");
  if (sliderType) {
    // remove yellowtree/ from string
    const type = sliderType.split("/").pop();

    el?.classList.add(`${type}-swiper`);

    if (type === "teaser") {
      const swiperElement = el.shadowRoot?.querySelector(".swiper");

      if (swiperElement) {
        swiperElement.setAttribute("style", "overflow: visible");
      }
    }
  }

  if (el.classList.contains("teaser-swiper")) {
    Object.assign(el, swiperTeaserParams);
  } else {
    Object.assign(el, swiperParams);
  }

  // @ts-ignore
  el.initialize();
}

function getSwiper(el: HTMLElement | null | undefined) {
  return (el?.querySelector("swiper-container") as { swiper: Swiper } | null)
    ?.swiper;
}

function SliderEditor(props: BlockEditProps<BlockAttributes>) {
  const blockProps = useBlockProps();
  const innerBlocksProps = useInnerBlocksProps(
    {},
    {
      allowedBlocks: ["yellowtree/teaser", "yellowtree/iconcard"],
      renderAppender() {
        return null;
      },
      // @ts-ignore
      orientation: "horizontal",
    }
  );
  const ref = useRef<HTMLDivElement>(null);
  const children = useBlockEditorSelect(
    (select) => select.getBlockOrder(props.clientId),
    [props.clientId]
  );

  const dispatch = useDispatch(blockEditorStore);
  function getSliderType(sliderContainer: Element | null | undefined) {
    const swiperContainer = sliderContainer;
    const firstChild = swiperContainer?.firstElementChild?.firstElementChild;
    if (firstChild) {
      const yellowtreeClass =
        Array.from(firstChild.classList).find((className) =>
          className.startsWith("wp-block-yellowtree")
        ) || "";

      const type = yellowtreeClass?.split("-").pop() || "";
      addSlide(type);
    }
  }

  const addSlide = useCallback(
    (type: string) => {
      dispatch.insertBlock(
        createBlock(`yellowtree/${type}`),
        children.length,
        props.clientId
      );
    },
    [children.length, dispatch, props.clientId]
  );

  useLayoutEffect(() => {
    domReady(ref.current, () =>
      initSwiper(ref.current?.querySelector("swiper-container"))
    );
    getSwiper(ref.current)?.update();
  }, [innerBlocksProps, ref]);

  useLayoutEffect(() => {
    getSwiper(ref.current)?.update();
  }, [children]);

  return (
    <div {...blockProps}>
      <div ref={ref}>
        <swiper-container
          init={false}
          {...innerBlocksProps}
          className={innerBlocksProps.className}
        >
          {
            // @ts-ignore
            innerBlocksProps.children
          }
          <swiper-slide>
            <button
              className="btn btn-primary w-100"
              onClick={(e) => {
                getSliderType(e.currentTarget.closest("swiper-container"));
              }}
            >
              Slide hinzufügen
            </button>
          </swiper-slide>
        </swiper-container>
        <div className="swiper-scrollbar"></div>
      </div>
    </div>
  );
}

addFilter(
  "editor.BlockListBlock",
  "yellowtree/slider-wrap-children",
  createHigherOrderComponent((BlockListBlock) => {
    return (props: JSX.IntrinsicAttributes) => {
      const parentName = useParentBlockName((props as any).clientId);
      if (parentName !== "yellowtree/slider") {
        return <BlockListBlock {...props} />;
      }
      return (
        <swiper-slide prevent-clicks="false" prevent-clicks-propagation="false">
          <BlockListBlock {...props} />
        </swiper-slide>
      );
    };
  }, "withStyleClasses")
);

registerBlockType<BlockAttributes>(config.name, {
  ...config,
  icon: SliderIcon,
  edit: SliderEditor,
  save() {
    return <InnerBlocks.Content />;
  },
});

registerBlockVariations();
